/**
 * Parsing a pasted video address.
 *
 * Deliberately strict, and it returns null rather than guessing. Building an
 * embed URL out of something that is not a video address is how a page ends up
 * framing an arbitrary third-party site, and this value comes from a text box
 * in an admin panel.
 *
 * The empty string and the internal placeholder both parse to null, because
 * "no video yet" is a real, supported state: the section renders its cover
 * photograph, the field stays visible in the panel, and no VideoObject is
 * emitted. An iframe with an empty src is not an acceptable stand-in. In a
 * server-rendered site it resolves to the current document, which means a
 * second full render of the page inside itself.
 */

export const VIDEO_URL_PLACEHOLDER = "YOUTUBE_VIDEO_URL_PLACEHOLDER";

export type VideoSource = {
  kind: "youtube" | "vimeo";
  id: string;
  embedUrl: string;
  watchUrl: string;
};

/** youtube-nocookie, so a view does not set an advertising cookie on this visitor. */
const youtube = (id: string): VideoSource => ({
  kind: "youtube",
  id,
  embedUrl: `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`,
  watchUrl: `https://www.youtube.com/watch?v=${id}`,
});

export function parseVideoUrl(raw?: string | null): VideoSource | null {
  const value = (raw ?? "").trim();
  if (!value || value === VIDEO_URL_PLACEHOLDER) return null;

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  // http:, and anything else, is refused outright. An embed is a request the
  // page makes on the visitor's behalf, and it is not made in the clear.
  if (url.protocol !== "https:") return null;

  const host = url.hostname.replace(/^www\./, "");

  if (host === "youtu.be") {
    const id = url.pathname.slice(1);
    return /^[\w-]{6,20}$/.test(id) ? youtube(id) : null;
  }

  if (host === "youtube.com" || host === "m.youtube.com" || host === "youtube-nocookie.com") {
    const id = url.searchParams.get("v") ?? url.pathname.replace(/^\/(embed|v|shorts)\//, "");
    return /^[\w-]{6,20}$/.test(id) ? youtube(id) : null;
  }

  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const id = url.pathname.replace(/^\/(video\/)?/, "").split("/")[0];
    return /^\d{5,12}$/.test(id)
      ? {
          kind: "vimeo",
          id,
          embedUrl: `https://player.vimeo.com/video/${id}?autoplay=1&dnt=1`,
          watchUrl: `https://vimeo.com/${id}`,
        }
      : null;
  }

  return null;
}
